import { ArrowUturnLeftIcon } from "@heroicons/react/16/solid";
import { ClockIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import CommonProjectInvoiceDetails from "modules/billing/components/CommonProjectInvoiceDetails";
import { useCallback, useState } from "react";

import Button from "~/components/buttons/Button";
import Hint from "~/components/Hint";
import WordIcon from "~/components/icons/WordIcon";
import LoadingSpinner from "~/components/LoadingSpinners/LoadingSpinner";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import Tooltip from "~/components/Tooltip";
import { updateTimeTrackingsEditableStatus } from "~/modules/billing/api/draftInvoice/draftInvoiceApiDispatchers.ts";
import { ALL_DRAFT_INVOICES_QUERY_KEY } from "~/modules/billing/api/draftInvoice/draftInvoiceQueries.ts";
import { All_PENDING_INVOICES_QUERY_KEY } from "~/modules/billing/api/pendingInvoice/pendingInvoiceQueries.ts";
import type { UnprocessedInvoicesIndexTableData } from "~/modules/billing/components/unprocessedInvoicesTableTypes.ts";
import { useInvoiceDownload } from "~/modules/billing/hooks/useInvoiceDownload.ts";
import type { User, UserWithConfidentialInformation } from "~/modules/user/api/user/userTypes.ts";

type Props = {
	allUsersData: User[] | UserWithConfidentialInformation[];
	onClose: () => void;
	onRevertStatusClick: () => void;
	onStartBillingProcessClick: () => void;
	draftInvoiceData: UnprocessedInvoicesIndexTableData;
	userCanManageInvoices:boolean
};

const DraftProjectInvoiceDetails = ({
	allUsersData,
	onClose,
	onRevertStatusClick,
	onStartBillingProcessClick,
	draftInvoiceData,
	userCanManageInvoices
}: Props) => {
	const { isGenerating, handleDownload } = useInvoiceDownload(draftInvoiceData.id);
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [serverErrorMessage, setServerErrorMessage] = useState<string>("");
	const queryClient = useQueryClient();

	const handleUpdateTimeTrackingsEditableStatusClick = useCallback(async (enabled: boolean) => {
		try {
			setIsBusy(true);

			await updateTimeTrackingsEditableStatus(draftInvoiceData.id, enabled);
			await queryClient.refetchQueries({ queryKey: ALL_DRAFT_INVOICES_QUERY_KEY });

			if (!enabled) {
				queryClient.invalidateQueries({ queryKey: All_PENDING_INVOICES_QUERY_KEY });
			}
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
		}
		setIsBusy(false);
	}, [draftInvoiceData.id, queryClient]);


	const isSubmittable = !draftInvoiceData.timeTrackingsEditable;

	return <>
		<SidebarHeader>
			<div className="flex items-center justify-between">
				<SidebarHeaderHeadline>Rechnungsdetails</SidebarHeaderHeadline>
				{!draftInvoiceData.timeTrackingsEditable && userCanManageInvoices && <div className="ml-auto flex items-center gap-x-2">
					<Tooltip text="Erfassungen temporär entsperren">
						<div>
							<Button theme="danger"
									onClick={() => handleUpdateTimeTrackingsEditableStatusClick(true)}
									size="sm">
								<ClockIcon className="size-5" />
							</Button>
						</div>
					</Tooltip>
					<Tooltip text="Rechnungsstatus zurücksetzen">
						<div>
							<Button theme="danger"
									onClick={onRevertStatusClick}
									size="sm">
								<ArrowUturnLeftIcon className="size-5" />
							</Button>
						</div>
					</Tooltip>
				</div>}
			</div>
		</SidebarHeader>
		<div className="grow overflow-auto px-7 py-2">
			{isBusy && <SidebarBusyOverlay />}
			{serverErrorMessage &&
				<SidebarErrorOverlay title="Update fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
			{draftInvoiceData.hasOverbookedOrder && <><Hint size="sm"
														   theme="error">Das Kostenvolumen der Bestellung ist
				überbucht.</Hint>
				<div className="h-2" />
			</>}
			{draftInvoiceData.timeTrackingsEditable && <Hint size="sm"
															 theme="error"><p>Das Bearbeiten und Erstellen von
				Erfassungen für diese Rechnung wurde temporär aktiviert, um nachträgliche Änderungen zu
				ermöglichen.</p>
				<br />
				<p>Die Rechnungsstellung kann erst abgeschlossen werden, sobald alle Änderungen vorgenommen wurden und
					die Erfassungen wieder gesperrt werden.</p>
				<br />
				<p><strong>Der Rechnungssbetrag wird bei der Sperrung neu berechnet und es sollte ein neues
					Rechnungsdokument
					erzeugt werden.</strong></p>
				<br />
				<Button theme="primary"
						onClick={() => handleUpdateTimeTrackingsEditableStatusClick(false)}>Erfassungen jetzt
					sperren</Button>
				<p className="h-3" />
			</Hint>}

			<CommonProjectInvoiceDetails allUsersData={allUsersData}
										 invoiceData={draftInvoiceData}
										 staffingsClosingStatuses={draftInvoiceData.staffingsClosingStatuses}>
			</CommonProjectInvoiceDetails>
		</div>
		<SidebarFooter>
			<div className="mr-auto">
				<Button theme="none"
						disabled={!isSubmittable}
						onClick={handleDownload}><WordIcon className="size-5" />Rechnungs Dokument{isGenerating &&
					<LoadingSpinner scale={0.7}
									theme="neutral" />}</Button>
			</div>
			{userCanManageInvoices && <Button theme="primary"
					disabled={!isSubmittable || isBusy}
					onClick={onStartBillingProcessClick}
			>
				Rechnungsstellung abschließen
			</Button>}
			<Button theme="none"
					onClick={onClose}>
				{userCanManageInvoices ? "Abbrechen" :"Schließen"}
			</Button>
		</SidebarFooter>
	</>;
};

export default DraftProjectInvoiceDetails;